import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  RefObject,
} from 'react';

import { localStorageWrapper } from 'approot/shared/storage';
import { useGetClassStudents } from 'approot/students/use-get-class-students';
import { useMediaQuery } from 'approot/shared/style';
import { useActivityBuilderPresentContext } from '../present/activity-builder-present.context';
import { ASSESSMENT_ACTIVITIES, LESSON_ACTIVITIES } from 'global/constants';
import useUserData from 'approot/shared/user/user-data';

type UseIntroStudentSnackbarReturn = {
  shouldShow: boolean;
  handleDismiss: () => void;
  isLoading: boolean;
  snackbarRef: RefObject<HTMLDivElement | null>;
};

const STORAGE_KEY_PREFIX = 'inq-intro-student-snackbar-dismissed-class-';
const MIN_SPACING_PX = 24;

export const useIntroStudentSnackbar = (): UseIntroStudentSnackbarReturn => {
  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const [hasInsufficientSpacing, setHasInsufficientSpacing] =
    useState<boolean>(false);
  const { classId, contentType } = useActivityBuilderPresentContext();
  const snackbarRef = useRef<HTMLDivElement>(null);
  const { isStudentSignedIn } = useUserData();

  const { hasStudents, loading } = useGetClassStudents({
    classId: classId || undefined,
  });

  // Use existing media query hook to check if screen is large enough
  const isScreenLargeEnough = useMediaQuery('(min-width: 800px)');

  const storageKey = classId ? `${STORAGE_KEY_PREFIX}${classId}` : null;

  // Function to check spacing between snackbar and Get Started button
  const checkSpacing = useCallback(() => {
    if (!snackbarRef.current) return;

    const snackbarElement = snackbarRef.current;
    const getStartedButton = document.getElementById(
      'btn-get-started'
    ) as HTMLElement;

    if (!getStartedButton) {
      setHasInsufficientSpacing(true);
      return;
    }

    const snackbarRect = snackbarElement.getBoundingClientRect();
    const buttonRect = getStartedButton.getBoundingClientRect();

    // Calculate the spacing between the bottom of the snackbar and the top of the Get Started button
    const spacing = buttonRect.top - snackbarRect.bottom;

    setHasInsufficientSpacing(spacing < MIN_SPACING_PX);
  }, []);

  // Check localStorage on mount
  useEffect(() => {
    if (!storageKey) return;

    const dismissedValue = localStorageWrapper.getItem(storageKey);
    if (dismissedValue === 'true') {
      setIsDismissed(true);
    }
  }, [storageKey]);

  const handleDismiss = useCallback(() => {
    if (!storageKey) return;

    localStorageWrapper.setItem(storageKey, 'true');
    setIsDismissed(true);
  }, [storageKey]);

  // Determine if snackbar should show
  const shouldShow = useMemo(() => {
    return (
      !loading &&
      !isDismissed &&
      !hasStudents &&
      !!classId &&
      !isStudentSignedIn &&
      (contentType === LESSON_ACTIVITIES ||
        contentType === ASSESSMENT_ACTIVITIES) &&
      !hasInsufficientSpacing &&
      isScreenLargeEnough
    );
  }, [
    loading,
    isDismissed,
    hasStudents,
    classId,
    contentType,
    hasInsufficientSpacing,
    isScreenLargeEnough,
    isStudentSignedIn,
  ]);

  // Monitor spacing on mount and when snackbar becomes visible
  useEffect(() => {
    if (!shouldShow) return;

    // Check spacing immediately
    checkSpacing();

    // Set up ResizeObserver to monitor layout changes
    const resizeObserver = new ResizeObserver(() => {
      checkSpacing();
    });

    // Observe the snackbar element
    if (snackbarRef.current) {
      resizeObserver.observe(snackbarRef.current);
    }

    // Also observe the Get Started button if it exists
    const getStartedButton = document.getElementById('btn-get-started');
    if (getStartedButton) {
      resizeObserver.observe(getStartedButton);
    }

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [shouldShow, checkSpacing]);

  return {
    shouldShow,
    handleDismiss,
    isLoading: loading,
    snackbarRef,
  };
};
